import {
  call,
  put,
  takeEvery,
  takeLeading,
  all,
  debounce,
} from 'redux-saga/effects';
import { isEmbeddedInCommandCenter } from 'util/commandCenter';
import {
  deskingSuccess,
  deskingFailure,
  deskingSendToSalespersonSuccess,
  deskingSendToSalespersonFailure,
  deskingOfferCreateSuccess,
  deskingOfferCreateFailure,
  deskingOfferDeleteSuccess,
  deskingOfferDeleteFailure,
  pushCrmDeskingOfferSuccess,
  pushCrmDeskingOfferFailure,
  setDeskingOffers,
  setPartialOffers,
  bulkDeskingOfferUpdateFailure,
  bulkDeskingOfferUpdateSuccess,
} from './actions';
import {
  deskingPaymentsSuccess,
  deskingPaymentsFailure,
  deskingOfferPaymentSuccess,
  deskingOfferPaymentFailure,
  deskingOfferPaymentDeleteSuccess,
  deskingOfferPaymentDeleteFailure,
  deskingBulkPaymentSuccess,
  deskingBulkPaymentFailure,
  deskingOfferPaymentCalcStart,
  deskingOfferPaymentCalcEnd,
  setLatestRequestDate,
} from '../deskingPayments/actions';
import {
  DESKING_REQUEST,
  DESKING_SEND_TO_SALESPERSON_REQUEST,
  DESKINGOFFER_CREATE_REQUEST,
  DESKINGOFFER_CREATE_DEFAULT_REQUEST,
  DESKINGOFFER_BULK_UPDATE_REQUEST,
  DESKINGOFFER_DELETE_REQUEST,
  DESKINGOFFER_CRM_PUSH_REQUEST,
} from './actionTypes';
import {
  pencilCreateSuccess,
  pencilCreateFailure,
  pencilCRMPushSuccess,
  pencilCRMPushFailure,
  setPencilModalText,
} from '../deals/actions';
import DeskingService from '../../services/DeskingService';

export function* getDeskingTask(action) {
  const { dealId, carId } = action.payload;
  try {
    const response = yield call(DeskingService.getDesking, dealId, carId);
    yield put(deskingSuccess(response.desking));
    yield put(deskingPaymentsSuccess(response.payments));
  } catch (error) {
    yield put(deskingFailure(error));
    yield put(deskingPaymentsFailure(error));
  }
}

export function* sendToSalespersonDeskingTask(action) {
  try {
    // Reset modal
    yield put(setPencilModalText(''));

    const { deskingId } = action.payload;
    const newPencil = yield call(DeskingService.sendToSalesperson, deskingId);
    yield put(
      pencilCreateSuccess({
        pencil: newPencil,
        msg: isEmbeddedInCommandCenter()
          ? 'Deal has been successfully sent to salesperson'
          : 'Desking Offers Sent', // getPencilUpdateSuccessMsg(deal),
      }),
    );
    yield put(deskingSendToSalespersonSuccess(newPencil));
  } catch (err) {
    yield put(
      pencilCreateFailure({
        err,
        msg: 'Failed to Send Desking Offers', // getPencilUpdateFailureMsg(deal),
      }),
    );
    yield put(deskingSendToSalespersonFailure(err));
  }
}

export function* createDeskingOfferTask(action) {
  try {
    const { deskingOffer } = action.payload;
    const response = yield call(
      DeskingService.createDeskingOffer,
      deskingOffer,
    );
    yield put(deskingOfferCreateSuccess(response.deskingOffer));
    const paymentWithId = {
      ...response.payment,
      deskingOfferId: response.deskingOffer.id,
    };
    yield put(deskingOfferPaymentSuccess(paymentWithId));
  } catch (err) {
    yield put(deskingOfferCreateFailure(err));
    yield put(deskingOfferPaymentFailure(err));
  }
}

export function* createDefaultDeskingOfferTask(action) {
  try {
    const { dealId, carId, deskingId, financeType } = action.payload;
    const response = yield call(DeskingService.createDefaultDeskingOffer, {
      dealId,
      carId,
      deskingId,
      financeType,
    });
    yield put(deskingOfferCreateSuccess(response.deskingOffer));
    const paymentWithId = {
      ...response.payment,
      deskingOfferId: response.deskingOffer.id,
    };
    yield put(deskingOfferPaymentSuccess(paymentWithId));
  } catch (err) {
    yield put(deskingOfferCreateFailure(err));
    yield put(deskingOfferPaymentFailure(err));
  }
}

export function* bulkUpdateDeskingOffers(action) {
  let deskingOfferIds;
  try {
    const { deskingOffers, params } = action.payload;
    const computePayments = params?.computePayments ?? false;
    const partialUpdate = params?.partialUpdate ?? false;
    const saveToGlobalState = params?.saveToGlobalState ?? true;
    const isMSIncentivesEnabled = params?.isMSIncentivesEnabled ?? false;
    if (saveToGlobalState) {
      if (partialUpdate) {
        yield put(setPartialOffers(deskingOffers));
      } else {
        yield put(setDeskingOffers(deskingOffers));
      }
    }
    deskingOfferIds = deskingOffers.map(deskingOffer => deskingOffer.id);
    // insert offers in progress
    yield put(deskingOfferPaymentCalcStart(deskingOfferIds));

    const requestDate = new Date();
    yield put(setLatestRequestDate({ requestDate, deskingOfferIds }));

    const collection = yield call(DeskingService.bulkUpdateDeskingOffers, {
      deskingOffers,
      computePayments,
      partialUpdate,
      isMSIncentivesEnabled,
    });
    yield put(bulkDeskingOfferUpdateSuccess());
    if (computePayments) {
      yield all(
        collection.map(({ payment, deskingOffer }) => {
          const paymentWithId = { ...payment, deskingOfferId: deskingOffer.id };
          return put(
            deskingBulkPaymentSuccess({ payment: paymentWithId, requestDate }),
          );
        }),
      );
    }
    // call back the resolve function to simulate the promise behavior
    action.payload.resolve?.(collection);
  } catch (err) {
    yield put(bulkDeskingOfferUpdateFailure(err));
    yield put(deskingBulkPaymentFailure(err));
    // call back the resolve function to simulate the promise behavior
    action.payload.reject?.(err);
  } finally {
    // remove offers in progress
    yield put(deskingOfferPaymentCalcEnd(deskingOfferIds));
  }
}

export function* deleteDeskingOfferTask(action) {
  try {
    const { deskingOffer } = action.payload;
    yield call(DeskingService.deleteDeskingOffer, deskingOffer.id);
    yield put(deskingOfferDeleteSuccess(deskingOffer));
    yield put(deskingOfferPaymentDeleteSuccess(deskingOffer.id));
  } catch (err) {
    yield put(deskingOfferDeleteFailure(err));
    yield put(deskingOfferPaymentDeleteFailure(err));
  }
}

export function* pushDeskingToCRMTask(action) {
  try {
    // Reset modal
    yield put(setPencilModalText(''));
    const { deskingOffer } = action.payload;
    const result = yield call(
      DeskingService.pushDeskingOfferToCRM,
      deskingOffer.id,
    );
    yield put(pushCrmDeskingOfferSuccess());
    yield put(pencilCRMPushSuccess());
    if (result && result.deskingURL) {
      setTimeout(() => {
        const newWindow = window.open();
        newWindow.opener = null;
        newWindow.location = result.deskingURL;
      }, 0);
    }
  } catch (err) {
    yield put(pushCrmDeskingOfferFailure(err));
    yield put(pencilCRMPushFailure(err));
  }
}

export function* deskingSaga() {
  yield takeEvery(DESKING_REQUEST, getDeskingTask);
  yield takeLeading(
    DESKING_SEND_TO_SALESPERSON_REQUEST,
    sendToSalespersonDeskingTask,
  );
  yield debounce(
    1000,
    DESKINGOFFER_CREATE_DEFAULT_REQUEST,
    createDefaultDeskingOfferTask,
  );
  yield debounce(1000, DESKINGOFFER_CREATE_REQUEST, createDeskingOfferTask);
  yield takeEvery(DESKINGOFFER_DELETE_REQUEST, deleteDeskingOfferTask);
  yield takeEvery(DESKINGOFFER_CRM_PUSH_REQUEST, pushDeskingToCRMTask);
  yield takeEvery(DESKINGOFFER_BULK_UPDATE_REQUEST, bulkUpdateDeskingOffers);
}
